/**
 * Polling utilities for aggregator status queries
 * Provides reusable polling logic for aggregator iframe views
 */

import { QueryObserverResult } from '@tanstack/react-query';
import { AggregatorStatusResponse } from 'src/interfaces/aggregator.interfaces';
import {
  AGGREGATOR_POLLING_INTERVAL,
  AGGREGATOR_MAX_POLLING_DURATION,
  AGGREGATOR_STATUS,
} from './aggregator.constants';

/**
 * Creates a refetchInterval function for aggregator status polling
 * Polls until status is complete, failed, or lender has responded, or max duration is reached
 *
 * @param pollingStartTime - Timestamp when polling started (from useState(Date.now()))
 * @returns A function that returns polling interval or false to stop polling
 */
export const createAggregatorPollingConfig = (pollingStartTime: number) => {
  return (query: QueryObserverResult<AggregatorStatusResponse>): number | false => {
    const data = query.data;
    const elapsed = Date.now() - pollingStartTime;

    // Continue polling if no data yet
    if (!data) {
      return AGGREGATOR_POLLING_INTERVAL;
    }

    // Stop polling if:
    // 1. Submission state is completed, failed, or Lender Responded
    // 2. Lender response is explicitly not pending (approved/declined/Pre Approved)
    // 3. Maximum polling duration exceeded
    const shouldStopPolling =
      data.submission_state === AGGREGATOR_STATUS.SUBMISSION_STATE.COMPLETED ||
      data.submission_state === AGGREGATOR_STATUS.SUBMISSION_STATE.FAILED ||
      data.submission_state === AGGREGATOR_STATUS.SUBMISSION_STATE.LENDER_RESPONDED ||
      (data.lender_response && data.lender_response !== AGGREGATOR_STATUS.LENDER_RESPONSE.PENDING) ||
      elapsed >= AGGREGATOR_MAX_POLLING_DURATION;

    if (!shouldStopPolling) {
      return AGGREGATOR_POLLING_INTERVAL;
    }

    return false;
  };
};
